import { WinNativeFocusTarget } from '~/constants/nativeApi';
import popupWindowService from '~/services/popupWindowService';
import BootstrapActions from '~/stores/actions/BootstrapActions';
import ChannelMembersActions from '~/stores/actions/ChannelMembersActions';
import EmoticonStickerActions from '~/stores/actions/EmoticonStickerActions';
import MailDomainActions from '~/stores/actions/MailDomainActions';
import MeteringActions from '~/stores/actions/MeteringActions';
import ObserveActions from '~/stores/actions/ObserveActions';
import OfficeHoursActions from '~/stores/actions/OfficeHoursActions';
import PermissionActions from '~/stores/actions/PermissionActions';
import PopperActions from '~/stores/actions/PopperActions';
import ProxyActions from '~/stores/actions/ProxyActions';
import ShortcutActions from '~/stores/actions/ShortcutActions';
import ThreadChannelActions from '~/stores/actions/ThreadChannelActions';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { isElectron, isWinNative } from '~/utils/runEnvironmentUtil';
import QS from 'qs';
import isNil from 'ramda/es/isNil';

import { createChannelActions } from './actions/ChannelActions';
import CoachMarkActions from './actions/CoachMarkActions';
import DoorayCommandActions from './actions/DoorayCommandActions';
import DraftLogActions from './actions/DraftLogActions';
import EditorActions from './actions/EditorActions';
import ErrorActions from './actions/ErrorActions';
import FavoriteActions from './actions/FavoriteActions';
import GnbActions from './actions/GnbAcations';
import HintActions from './actions/HintActions';
import HookActions from './actions/HookActions';
import LogActions from './actions/LogActions';
import MainEditorActions from './actions/MainEditorActions';
import MemberActions from './actions/MemberActions';
import MenuActions from './actions/MenuActions';
import ModalActions from './actions/ModalActions';
import NativeActions from './actions/NativeActions';
import NotificationActions from './actions/NotificationActions';
import OrganizationChartActions from './actions/OrganizationChartActions';
import OrgsActions from './actions/OrgsActions';
import SearchActions from './actions/SearchActions';
import SettingActions from './actions/SettingActions';
import SlashCommandAppActions from './actions/SlashCommandAppActions';
import UploadActions from './actions/UploadActions';
import UserActions from './actions/UserActions';
import WebsocketActions, { WebsocketHandlerType } from './actions/WebsocketActions';
import Stores from './Stores';
import Utils from './Utils';

class Actions {
  constructor(private stores: Stores, private utils: Utils) {
    utils.initialize();
  }

  bootstrapActions = new BootstrapActions(this);
  doorayCommandActions = new DoorayCommandActions(this);
  channelActions = createChannelActions(this);
  draftLogActions = new DraftLogActions();
  editorActions = new EditorActions(this);
  errorActions = new ErrorActions(this);
  favoriteActions = new FavoriteActions(this);
  gnbActions = new GnbActions(this);
  hintActions = new HintActions(this);
  hookActions = new HookActions(this);
  slashCommandAppActions = new SlashCommandAppActions(this);
  mainEditorActions = new MainEditorActions('main', this, { detailUiState: mainDetailUiState });
  threadMainEditorActions = new MainEditorActions('thread', this, { detailUiState: threadDetailUiState });
  logActions = new LogActions(this, {
    channelLogsStore,
    mainEditorActions: this.mainEditorActions,
    detailUiState: mainDetailUiState,
    context: 'main',
  });

  threadLogActions = new LogActions(this, {
    channelLogsStore: threadChannelLogsStore,
    mainEditorActions: this.threadMainEditorActions,
    detailUiState: threadDetailUiState,
    context: 'thread',
  });

  memberActions = new MemberActions(this);
  menuActions = new MenuActions(this);
  modalActions = new ModalActions(this);
  nativeActions = new NativeActions(this);
  notificationActions = new NotificationActions();
  organizationChartActions = new OrganizationChartActions(this);
  orgsActions = new OrgsActions(this);
  searchActions = new SearchActions(this);
  settingActions = new SettingActions();
  coachMarkActions = new CoachMarkActions(this);
  uploadActions = new UploadActions(this, {
    logActions: this.logActions,
    detailUiState: mainDetailUiState,
    channelLogsStore,
  });

  threadUploadActions = new UploadActions(this, {
    logActions: this.threadLogActions,
    detailUiState: threadDetailUiState,
    channelLogsStore: threadChannelLogsStore,
  });

  userActions = new UserActions(this);
  websocketActions = new WebsocketActions(WebsocketHandlerType.Default, this);
  interTenantWebsocketActions = new WebsocketActions(WebsocketHandlerType.InterTenant, this);
  meteringActions = new MeteringActions(this);
  channelMembersActions = new ChannelMembersActions(this);
  observeActions = new ObserveActions(this);
  shortcutActions = new ShortcutActions(this);
  proxyActions = new ProxyActions(this);
  permissionActions = new PermissionActions(this);
  officeHoursActions = new OfficeHoursActions(this);
  mailDomainActions = new MailDomainActions();
  threadChannelActions = new ThreadChannelActions(this);
  emoticonStickerActions = new EmoticonStickerActions(this);
  popperActions = new PopperActions(this);

  openPopupChannel = async (channelId: string, params?) => {
    const queryParams = QS.stringify(params);
    if (isElectron()) {
      const channel = await this.channelActions.getOrCreateDirectChannelByChannelId(channelId);
      const url = `/messenger/popup/${channel.channelId}?${queryParams}`;
      popupWindowService.open(url, channel.channelId);
    } else {
      const { channelId: realChannelId } = await this.channelActions.getOrCreateDirectChannelByChannelId(channelId);
      const popupWindow = popupWindowService.restoreWindowRef(realChannelId);
      if (isNil(popupWindow)) return;
      const url = `/messenger/popup/${realChannelId}?${queryParams}`;

      if (popupWindow.location.href === 'about:blank') {
        return isWinNative()
          ? popupWindowService.open(url, realChannelId, { width: 0, height: 0 })
          : popupWindowService.open(url, realChannelId);
      }

      popupWindow.name = realChannelId;
      popupWindow.location.href = url;
      isWinNative()
        ? this.nativeActions.sendSetFocus({
            channelId: realChannelId,
            target: WinNativeFocusTarget.POPUP,
          })
        : popupWindow.focus();
      return Promise.resolve(popupWindow);
    }
  };

  openSlashCommandIntegrationPopup = () => {
    const windowName = 'doorayMessenger:integration';
    const url = '/messenger/integration';
    if (isElectron()) {
      popupWindowService.open(url, windowName);
    }

    const popupWindow = popupWindowService.restoreWindowRef('doorayMessenger:integration');
    if (isNil(popupWindow)) return;

    if (popupWindow.location.href === 'about:blank') {
      if (isWinNative()) {
        popupWindowService.open(url, windowName, { width: 0, height: 0 });
        this.nativeActions.sendSetFocus({
          target: WinNativeFocusTarget.INTEGRATION,
        });
        return;
      }
      return popupWindowService.open(url, windowName);
    }

    popupWindow.name = windowName;
    isWinNative() &&
      this.nativeActions.sendSetFocus({
        target: WinNativeFocusTarget.INTEGRATION,
      });
    return Promise.resolve(popupWindow);
  };
}

export default Actions;
